import { Injectable } from '@angular/core';
import { ApolloQueryResult, OperationVariables } from '@apollo/client/core';
import { Store } from '@ngrx/store';
import { Query } from 'apollo-angular';
import { QueryOptionsAlone } from 'apollo-angular/types';
import { print } from 'graphql';
import { Observable } from 'rxjs';
import { filter, take, tap } from 'rxjs/operators';
import { Logger } from 'src/app/shared/tcs-logging/logger';
import { TcsEntity } from 'src/graphql/generated/graphql.generated';
import { createSafeProps } from 'src/lib/tcs-app-insights/app-insights-util';
import {
  EntitiesReceived,
  EntitySnapshot,
  EntitySnapshotDto,
} from 'src/util/entity-store/entity-store.actions';
import { AppState } from './appstate';
import { revive, reviveUnsafe } from './generated/record-reviver.generated';
import { flattenEntities } from './store-helpers';
import { TcsEntityRecord } from './generated/client-sync.generated';

@Injectable({ providedIn: 'root' })
export class StoreService {
  constructor(public readonly store: Store<AppState>, private _log: Logger) {}

  public upsertEntitiesFromQuery<TQuery, TVariables extends OperationVariables>({
    query,
    variables,
    queryOptions,
    isCancellable,
  }: {
    query: Query<TQuery, TVariables>;
    variables?: TVariables;
    queryOptions?: QueryOptionsAlone<TVariables, TQuery>;
    isCancellable?: boolean;
  }): Observable<ApolloQueryResult<TQuery>> {
    return this._upsertEntitiesFromGraphQL(query, variables, queryOptions, isCancellable);
  }

  public upsertEntitiesFromGraphQL<TQuery, TVariables extends OperationVariables>(
    query: Query<TQuery, TVariables>,
    variables?: TVariables,
    queryOptions?: QueryOptionsAlone<TVariables, TQuery>,
  ): Observable<ApolloQueryResult<TQuery>> {
    return this._upsertEntitiesFromGraphQL(query, variables, queryOptions);
  }

  public _upsertEntitiesFromGraphQL<TQuery, TVariables extends OperationVariables>(
    query: Query<TQuery, TVariables>,
    variables?: TVariables,
    queryOptions?: QueryOptionsAlone<TVariables, TQuery>,
    isCancellable?: boolean,
  ): Observable<ApolloQueryResult<TQuery>> {
    const logPrefix = `StoreService.upsertEntitiesFromGraphQL`;
    return (
      isCancellable ?? false
        ? query.watch(variables, queryOptions).valueChanges
        : query.fetch(variables, queryOptions)
    ).pipe(
      filter((r) => !r.loading),
      tap((result) => {
        try {
          if (result.data == null) {
            const error = result.errors != null ? result.errors[0].message : 'unknown';
            throw new Error(
              `${logPrefix}: did not reseive any data - error: ` +
                error +
                ' ' +
                query +
                ' var: ' +
                variables,
            );
          }

          this._upsertNestedJs(result.data);
        } catch (err: any) {
          // eslint-disable-next-line no-console
          console.error(err);
          const queryStr = print(query.document);
          this._log.error(
            `${logPrefix}: Error upserting data from query result: ${err.message}`,
            createSafeProps({
              errors: result.errors,
              query: queryStr,
            }),
          );
        }
      }),
      take(1),
    );
  }

  public upsertFromPubNub(entities: EntitySnapshotDto[]) {
    const logPrefix = `StoreService.upsertFromPubNub`;
    const snapshots: EntitySnapshot[] = [];
    let success = true;
    // eslint-disable-next-line @typescript-eslint/prefer-for-of
    for (let i = 0; i < entities.length; i++) {
      const snapshot = entities[i];
      try {
        if (snapshot.source === 'Removed') {
          snapshots.push({
            key: snapshot.key,
            source: snapshot.source,
            type: snapshot.type,
          });
        } else {
          const [record, errors] = reviveUnsafe({
            type: snapshot.type,
            state: snapshot.state,
          });
          if (record != null) {
            snapshots.push({
              key: record.id,
              source: 'Fetched',
              state: record,
              type: snapshot.type,
            });
          }
          if (errors.length > 0) {
            success = false;
            errors.forEach((err) => {
              this._log.error(
                `${logPrefix}: Error deserializing record from PubNub for ${snapshot.type}Record: ${err.message}`,
              );
            });
          }
        }
      } catch (err: any) {
        success = false;
        this._log.error(
          `${logPrefix}: Error serializing data from PubNub to ${snapshot.type}Record: ${err.message} (id: ${snapshot.key})`,
          createSafeProps({ state: snapshot.state }),
        );
      }
    }

    this.store.dispatch(new EntitiesReceived(snapshots));
    if (!success) {
      throw new Error(`${logPrefix}: Error serializing data to immutable records from PubNub`);
    }
  }

  public upsertEntities(entities: TcsEntity[]): boolean {
    const logPrefix = `StoreService.upsertEntities`;
    const snapshots: EntitySnapshot[] = [];
    let success = true;
    // eslint-disable-next-line @typescript-eslint/prefer-for-of
    for (let i = 0; i < entities.length; i++) {
      const entityJs = entities[i];
      try {
        const record = revive({
          type: entityJs.__typename,
          state: entityJs,
        });
        if (record == null) {
          throw new Error(`${logPrefix}: unable to revive record of type '${entityJs.__typename}'`);
        }
        snapshots.push({
          key: record.id,
          source: 'Fetched',
          state: record,
          type: entityJs.__typename,
        });
      } catch (err: any) {
        success = false;
        this._log.error(
          `${logPrefix}: Error serializing data to ${entityJs.__typename}Record: ${err.message} (id: ${entityJs.id})`,
          createSafeProps({ state: entityJs }),
        );
      }
    }
    this.store.dispatch(new EntitiesReceived(snapshots));
    if (!success) {
      throw new Error('Error serializing data to immutable record');
    }
    return success;
  }

  public upsertEntityRecords(entityRecords: TcsEntityRecord[], typeName: string): boolean {
    const logPrefix = `StoreService.upsertEntityRecords`;
    const snapshots: EntitySnapshot[] = [];
    let success = true;
    // eslint-disable-next-line @typescript-eslint/prefer-for-of
    for (let i = 0; i < entityRecords.length; i++) {
      const entityRecord = entityRecords[i];
      try {
        const record = revive({
          type: typeName,
          state: entityRecord,
        });
        if (record == null) {
          throw new Error(`${logPrefix}: unable to revive record of type '${typeName}'`);
        }
        snapshots.push({
          key: record.id,
          source: 'Fetched',
          state: record,
          type: typeName,
        });
      } catch (err: any) {
        success = false;
        this._log.error(
          `${logPrefix}: Error serializing data to ${typeName}Record: ${err.message} (id: ${entityRecord.id})`,
          createSafeProps({ state: entityRecord }),
        );
      }
    }
    this.store.dispatch(new EntitiesReceived(snapshots));
    if (!success) {
      throw new Error('Error serializing data to immutable record');
    }
    return success;
  }

  private _upsertNestedJs(data: Record<any, any>) {
    const flattened: TcsEntity[] = flattenEntities(data) as TcsEntity[];
    return this.upsertEntities(flattened);
  }
}
